import { useState } from 'react';
import { 
  LayoutDashboard, 
  ReceiptText, 
  Handshake, 
  Users, 
  PlusCircle, 
  Search,
  Menu,
  X,
  LogOut,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ViewType } from './types';
import Dashboard from './pages/dashboard-page';
import ExpenseList from './pages/expense-list-page';
import AddExpense from './pages/add-expense-page';
import Settlements from './pages/settlements-page';
import AccountManagement from './pages/account-management-page';
import MemberManagement from './pages/member-management-page';
import Login from './pages/login-page';
import Register from './components/Register';
import TransactionHistory from './pages/transaction-history-page';
import SystemAdminPage from './pages/system-admin-page';
import NotificationBell from './components/NotificationBell';
import MobileNav from './components/MobileNav';
import GroupSwitcher from './components/GroupSwitcher';
import { api } from './services/api';
import { registerApiLoadingHandler } from './services/api';
import AppShell from './components/AppShell';
import { useLedgerStore } from './hooks/use-ledger-store';
import { useLoading } from './hooks/use-loading';
import LoadingOverlay from './components/ui/LoadingOverlay';
import { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Breadcrumbs } from './components/ui/Breadcrumbs';

export default function App() {
  const store = useLedgerStore();
  const { view, setView, isAuthenticated, isHydrated, currentUser, logout, currentGroupId, setCurrentGroupId, refreshGroupMembers } = store;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [showLargeAvatar, setShowLargeAvatar] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expenseMonthFilter, setExpenseMonthFilter] = useState<{ year: number; month: number } | null>(null);

  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    registerApiLoadingHandler((loading, message) => {
      if (loading) showLoading(message ?? '請稍候...');
      else hideLoading();
    });
  }, [showLoading, hideLoading]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadName = async () => {
      if (!currentGroupId) {
        setGroupName(null);
        return;
      }
      try {
        const g = await api.groups.get(currentGroupId as string);
        if (mounted) setGroupName((g as any)?.name ?? null);
      } catch (e) {
        if (mounted) setGroupName(null);
      }
    };
    void loadName();
    return () => { mounted = false; };
  }, [currentGroupId]);

  // SSR hydration 完成前渲染與 Server 一致的空白畫面，避免 hydration mismatch
  if (!isHydrated) {
    return <AppShell />;
  }

  if (!isAuthenticated) {
    return view === 'register' ? <Register /> : <Login />;
  }

  const isSystemAdmin = currentUser?.systemRole === 'system_admin';

  const navItems = [
    { id: 'dashboard', label: '儀表板', icon: LayoutDashboard },
    { id: 'expenses', label: '支出紀錄', icon: ReceiptText },
    { id: 'settlements', label: '結算中心', icon: Handshake },
    { id: 'accounts', label: '我的帳戶', icon: Wallet },
    { id: 'members', label: '成員概覽', icon: Users },
  ];

  const renderView = () => {
    switch (view) {
      case 'dashboard': return <Dashboard store={store} setView={setView} onMonthFilter={(year, month) => { setExpenseMonthFilter({ year, month }); setView('expenses'); }} />;
      case 'expenses': return <ExpenseList store={store} setView={setView} monthFilter={expenseMonthFilter} onClearMonthFilter={() => setExpenseMonthFilter(null)} />;
      case 'add-expense': 
      case 'edit-expense': return <AddExpense store={store} setView={setView} />;
      case 'settlements': return <Settlements store={store} />;
      case 'accounts': return <AccountManagement store={store} />;
      case 'members': return <MemberManagement store={store} />;
      case 'system-admin': return isSystemAdmin ? <SystemAdminPage /> : <Dashboard store={store} setView={setView} />;
      case 'search': return <ExpenseList store={store} setView={setView} isSearchView={true} />;
      case 'history': return <TransactionHistory store={store} setView={setView} initialQuery={searchQuery} />;
      default: return <Dashboard store={store} setView={setView} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-background text-foreground font-sans overflow-x-hidden">
      {/* Sidebar - Desktop Only */}
      {!isMobile && (
        <aside className={`fixed left-0 top-0 h-full bg-muted border-r border-border transition-all duration-300 z-50 ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
          <div className="p-6 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-10 overflow-hidden">
              <button
                className="min-w-[40px] h-10 rounded-xl overflow-hidden hover:opacity-80 active:scale-95 transition-all"
                onClick={() => setView('dashboard')}
                title="回到儀表板"
              >
                <img src="/icons/icon-192.png" alt="家庭帳本" className="w-10 h-10 object-cover" />
              </button>
                {isSidebarOpen && (
                <div className="whitespace-nowrap overflow-hidden">
                  <h1 className="text-xl font-bold text-primary leading-tight truncate">{groupName ?? '家庭帳本'}</h1>
                  <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">家家要有帳本</p>
                </div>
              )}
            </div>

            <nav className="flex-1 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setView(item.id as ViewType)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    view === item.id 
                      ? 'bg-accent text-primary font-bold shadow-sm' 
                      : 'text-muted-foreground hover:bg-muted-foreground/10 hover:translate-x-1'
                  }`}
                >
                  <item.icon size={20} />
                  {isSidebarOpen && <span className="text-sm">{item.label}</span>}
                </button>
              ))}
              {isSystemAdmin && (
                <button
                  onClick={() => setView('system-admin')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    view === 'system-admin'
                      ? 'bg-accent text-amber-600 font-bold shadow-sm'
                      : 'text-amber-600/70 hover:bg-amber-500/10 hover:translate-x-1'
                  }`}
                >
                  <ShieldCheck size={20} />
                  {isSidebarOpen && <span className="text-sm">系統管理</span>}
                </button>
              )}
            </nav>

            <div className="mt-auto space-y-4">
              <Button 
                onClick={() => setView('add-expense')}
                className="w-full h-14 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground font-bold shadow-wash hover:scale-[1.02] active:scale-95 transition-all"
              >
                <PlusCircle size={20} className="mr-2" />
                {isSidebarOpen && <span>新增支出</span>}
              </Button>
              
              <button 
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-3 text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded-xl transition-all"
              >
                <LogOut size={20} />
                {isSidebarOpen && <span className="text-sm font-bold">登出系統</span>}
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${!isMobile ? (isSidebarOpen ? 'ml-64' : 'ml-20') : 'ml-0 pb-[calc(5.5rem+env(safe-area-inset-bottom))]'}`}>
        {/* Top Header */}
        <header className="sticky top-0 right-0 z-40 bg-background/70 backdrop-blur-xl border-b border-border flex justify-between items-center px-4 md:px-8 min-h-14 md:min-h-16 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-4">
            {!isMobile && (
              <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-muted-foreground">
                {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
            )}
            
            <div className="flex items-center gap-2 md:hidden">
              <button
                className="w-8 h-8 rounded-lg overflow-hidden hover:opacity-80 active:scale-95 transition-all"
                onClick={() => setView('dashboard')}
                title="回到儀表板"
              >
                <img src="/icons/icon-192.png" alt="家庭帳本" className="w-8 h-8 object-cover" />
              </button>
              <GroupSwitcher
                currentGroupId={currentGroupId ?? undefined}
                onGroupChange={async (gid) => { setCurrentGroupId(gid); await refreshGroupMembers(gid); }}
                triggerLabel={groupName ?? '群組'}
              />
            </div>

            <div className="relative hidden md:flex items-center gap-2 bg-muted/50 rounded-full px-3 py-1.5 border border-border/40 focus-within:ring-2 focus-within:ring-primary/20 transition-all w-80">
              <Search className="text-muted-foreground ml-1" size={16} />
              <input 
                className="bg-transparent border-none text-sm w-full focus:outline-none font-medium h-7" 
                placeholder="搜尋交易紀錄..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setView('history');
                  }
                }}
              />
              <div className="w-px h-4 bg-border/60 mx-1"></div>
              <button 
                onClick={() => setView('history')}
                className="text-[10px] font-black text-primary px-2 hover:opacity-70 transition-opacity"
              >
                查詢
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 md:gap-6">
            <div className="flex items-center gap-1 md:gap-2">
              {isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setView('history')}
                  className="text-muted-foreground"
                  title="搜尋交易紀錄"
                >
                  <Search size={20} />
                </Button>
              )}
              {isMobile && isSystemAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setView('system-admin')}
                  className="text-amber-600/80 hover:text-amber-600"
                  title="系統管理"
                >
                  <ShieldCheck size={20} />
                </Button>
              )}
              <NotificationBell />
              {!isMobile && (
                <GroupSwitcher
                  currentGroupId={currentGroupId ?? undefined}
                  onGroupChange={async (gid) => {
                    setCurrentGroupId(gid);
                    await refreshGroupMembers(gid);
                  }}
                  triggerLabel={groupName ?? '群組'}
                />
              )}
            </div>
            <div className="h-6 w-[1px] bg-border md:h-8"></div>
            <div className="flex items-center gap-2 md:gap-3">
              <div className="text-right hidden md:block">
                <p className="text-xs font-bold text-primary">{currentUser?.nickname || 'Guest'}</p>
                <p className="text-[10px] text-muted-foreground font-medium">活躍成員</p>
              </div>
              <Avatar 
                key={`${currentUser?.id}-${currentUser?.avatarUrl}`}
                className="h-8 w-8 md:h-9 md:w-9 rounded-xl border-2 border-accent overflow-hidden cursor-pointer hover:scale-105 transition-transform"
                onClick={() => isMobile ? setView('accounts') : setShowLargeAvatar(true)}
                title={isMobile ? '我的帳戶' : '檢視頭像'}
              >
                <AvatarImage src={currentUser?.avatarUrl ?? undefined} className="object-cover" />
                <AvatarFallback className="text-xs">{currentUser?.nickname?.[0] || 'U'}</AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* View Content */}
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: isMobile ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: isMobile ? 0 : -10 }}
              transition={{ duration: 0.2 }}
            >
              {view !== 'history' && (
                <div className="md:block hidden">
                  <Breadcrumbs currentView={view} onNavigate={setView} />
                </div>
              )}
              {renderView()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <MobileNav />

      {/* Profile Avatar Modal */}
      <Dialog open={showLargeAvatar} onOpenChange={(open) => { setShowLargeAvatar(open); if (open) setAvatarImgError(false); }}>
        <DialogContent className="max-w-md p-0 border-none bg-transparent shadow-none flex flex-col items-center"
          showCloseButton={false}
          >
           <div className="relative group p-4 w-full">
              <div className="relative aspect-square w-full rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white/10 bg-muted">
                {currentUser?.avatarUrl && !avatarImgError ? (
                  <img
                    src={currentUser.avatarUrl}
                    className="w-full h-full object-cover"
                    onError={() => setAvatarImgError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-8xl font-black text-muted-foreground/20">
                    {currentUser?.nickname?.[0]}
                  </div>
                )}
              </div>
              <div className="mt-6 text-center text-white">
                <h3 className="text-2xl font-black">{currentUser?.nickname}</h3>
                <p className="text-sm font-medium opacity-60">{currentUser?.email}</p>
                <Button
                  className="mt-4 rounded-xl bg-primary text-primary-foreground font-bold"
                  onClick={() => {
                    setShowLargeAvatar(false);
                    setView('accounts');
                  }}
                >
                  管理帳戶
                </Button>
              </div>
              <button 
                onClick={() => setShowLargeAvatar(false)}
                className="absolute -top-2 -right-2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all shadow-xl"
              >
                <X size={24} />
              </button>
           </div>
        </DialogContent>
      </Dialog>
      <LoadingOverlay />
    </div>
  );
}
